/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_MCPTT_GROUPS_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { IMCPTTGroupData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { MCGroupsAdd } from "../../api/icCheck";
import MappedValueDropdown from "../../ui/MappedValueDropdown";
import {
  CREATE_MCPTT_GROUP_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";

interface IProps {
  org: string;
}
const set = new Set();

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;
  let isFieldEmpty = true;
  if (
    data["MAPPED-P25-TG-ID"] !== "" ||
    data["MAPPED-DMR-TG-ID"] !== "" ||
    data["MAPPED-ROIP-TG-ID"] !== "" ||
    data["MAPPED-DFSI-TG-ID"] !== ""
  ) {
    isFieldEmpty = false;
  }
  const isConnectionFormInvalid = ADD_MCPTT_GROUPS_FORM_FIELDS.flatMap(
    (rows: { fields: any[] }) =>
      rows.fields.filter((obj) => obj.type !== "MappedValueDropdown")
  )
    .map((obj: any) => obj.isInputInvalid(data[obj.name]))
    .reduce((acc: any, el: any) => {
      return acc || el || isFieldEmpty;
    }, isFieldEmpty);
  return isConnectionFormInvalid;
};

const NewMCPTTGroup = ({ org }: IProps) => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);
  const [currentItem, setCurrentItem] = useState<string>("MAPPED-P25-TG-ID");
  const [connectionData, setConnectionData] = useState<IMCPTTGroupData>({
    "MCPTT-GID": "",
    "MAPPED-P25-TG-ID": "",
    "MAPPED-DMR-TG-ID": "",
    "MAPPED-ROIP-TG-ID": "",
    "MAPPED-DFSI-TG-ID": "",
  });

  const createMCPTTGroup = async () => {
    setLoading(true);
    try {
      await MCGroupsAdd({
        data: JSON.stringify({
          "MCPTT-GID": connectionData["MCPTT-GID"],
          ...(connectionData &&
            connectionData["MAPPED-P25-TG-ID"] && {
              "MAPPED-P25-TG-ID": connectionData["MAPPED-P25-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DMR-TG-ID"] && {
              "MAPPED-DMR-TG-ID": connectionData["MAPPED-DMR-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-ROIP-TG-ID"] && {
              "MAPPED-ROIP-TG-ID": connectionData["MAPPED-ROIP-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DFSI-TG-ID"] && {
              "MAPPED-DFSI-TG-ID": connectionData["MAPPED-DFSI-TG-ID"],
            }),
        }),
        token,
        org,
      });
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/mc-groups`,
        method: "POST",
        body: JSON.stringify({
          "MCPTT-GID": connectionData["MCPTT-GID"],
          ...(connectionData &&
            connectionData["MAPPED-P25-TG-ID"] && {
              "MAPPED-P25-TG-ID": connectionData["MAPPED-P25-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DMR-TG-ID"] && {
              "MAPPED-DMR-TG-ID": connectionData["MAPPED-DMR-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-ROIP-TG-ID"] && {
              "MAPPED-ROIP-TG-ID": connectionData["MAPPED-ROIP-TG-ID"],
            }),
          ...(connectionData &&
            connectionData["MAPPED-DFSI-TG-ID"] && {
              "MAPPED-DFSI-TG-ID": connectionData["MAPPED-DFSI-TG-ID"],
            }),
        }),
        token,
        org,
      });
      successAlert(CREATE_MCPTT_GROUP_TRANSLATIONS.successAlertMessage);
      navigate("/configurationManagement/mcpttGroups");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_MCPTT_GROUPS_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof IMCPTTGroupData;
                  tooltipText?: string;
                  label: string;
                  invalidText?: string | null;
                  isInputInvalid?: (
                    val?: string | string[],
                    data?: any
                  ) => boolean;
                  required?: boolean;
                  placeholder: string[];
                  type: string;
                  items: string[];
                  dropdownDisabled: boolean;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
                  >
                    {rows.fields.map((field) => {
                      switch (field.type) {
                        case "MappedValueDropdown":
                          return (
                            <MappedValueDropdown
                              onChange={(val: any) => {
                                set.add(currentItem);
                                setConnectionData({
                                  ...connectionData,
                                  [currentItem]: val,
                                });
                              }}
                              handleResetData={() =>
                                setConnectionData({
                                  ...connectionData,
                                  [currentItem]: "",
                                })
                              }
                              items={field.items}
                              setCurrentItem={setCurrentItem}
                              currentItem={currentItem}
                              placeholder={field.placeholder}
                              invalid={
                                set.has(currentItem) &&
                                field.items &&
                                connectionData &&
                                field.isInputInvalid?.(
                                  field.items,
                                  connectionData
                                )
                              }
                              value={
                                connectionData[
                                  currentItem as keyof typeof connectionData
                                ] ?? ""
                              }
                              label={field.label}
                              tooltipText={field.tooltipText}
                              required
                              dropdownDisabled={field.dropdownDisabled}
                            />
                          );
                        default:
                          return (
                            <TextField
                              onChange={(val) => {
                                set.add(field.name);
                                setConnectionData({
                                  ...connectionData,
                                  [field.name]: val,
                                });
                              }}
                              placeholder={field.label}
                              value={connectionData[field.name] as string}
                              invalid={
                                set.has(field.name) &&
                                field.isInputInvalid?.(
                                  connectionData[field.name] as string
                                )
                              }
                              invalidText={
                                field.invalidText ||
                                FORM_VALIDATION_TRANSLATIONS.required(
                                  field.label
                                )
                              }
                              labelText={field.label}
                              tooltipText={field.tooltipText}
                              required={field.required}
                            />
                          );
                      }
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_MCPTT_GROUP_TRANSLATIONS.deleteModal.header}
        modalDescriptionText={
          CREATE_MCPTT_GROUP_TRANSLATIONS.deleteModal.description
        }
        onDelete={() => navigate("/configurationManagement/mcpttGroups")}
        open={open}
        setOpen={setOpen}
        confirmationButtonLabel={
          CREATE_MCPTT_GROUP_TRANSLATIONS.deleteModal.goBackButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_MCPTT_GROUP_TRANSLATIONS.cancelButtonLabel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateMCGroup-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createMCPTTGroup}
            label={CREATE_MCPTT_GROUP_TRANSLATIONS.createButtonLabel}
            dataCy="createMCGroup-"
          />
        </div>
      </div>
    </>
  );
};

export default NewMCPTTGroup;
